import {
	InvoiceTemplates,
	PnlGroups,
} from '@shared/utils/constants';
import { formatCurrency } from '@shared/utils/currency';
import { ensureNegative, round } from '@shared/utils/math';
import { formatPercentage } from '@shared/utils/formatPercentage';
import TemplateItem from '../TemplateItem';
import HireInvoiceItem from './HireInvoiceItem';

class HIIFreightBrokerCommission extends HireInvoiceItem {
	static get itemType() {
		return 'freightBrokerCommission';
	}

	constructor(id, isOriginal, params) {
		super(id, isOriginal, { fixtureCurrency: params?.fixtureCurrency });

		this.brokerId = params.brokerId;
		this.cargoId = params.cargoId;
		this.brokerName = params.brokerName;
		this.percentage = params.percentage;
		this.invoiceablePercentage = params.invoiceablePercentage;
		this.maxPercentage = params.maxPercentage;

		this._showTotal = true;
		this.itemTypeLabel = 'Broker Commission';
		this.addManually = false;
		this.templateSection = TemplateItem.Sections.COMMISSIONS;
		this.pnlGroup = PnlGroups.BROKER_COMMISSIONS;

		this._fields = {};
	}

	_calculateTotal(invoiceItems, convertCurrency) {
		const freight = this.getParent(invoiceItems);

		const finalPercentage = this.percentage * (this.invoiceablePercentage / 100);

		const totalFreight = freight.getCommissionBase(convertCurrency);

		const total = totalFreight * (finalPercentage / 100);

		return round(-1 * total, 2);
	}

	getDescription(invoiceItems) {
		const parent = this.getParent(invoiceItems);

		if (parent?.children.filter((c) => c instanceof HIIFreightBrokerCommission).length > 1) {
			return `${formatPercentage(this.percentage, 2)}, ${this.brokerName}`;
		}

		return `${formatPercentage(this.percentage, 2)}`;
	}

	static async _getClassParams(model, parentModel) {
		const broker = await model.getBroker(parentModel);

		return {
			cargoId: model.cargoId,
			brokerId: model.brokerId,
			brokerName: broker?.name,
			percentage: model.percentage ?? broker?.commission,
			invoiceablePercentage: model.invoiceablePercentage,
		};
	}

	async saveModel(model) {
		await super.saveModel(model);

		model.brokerId = this.brokerId;
		model.cargoId = this.cargoId;

		model.invoiceablePercentage = this.invoiceablePercentage;
		model.percentage = this.percentage;

		await model.save();
	}

	_getTemplateItemParams(invoiceItems) {
		const totalFreight = this.getParent(invoiceItems).getCommissionBase();

		return {
			[InvoiceTemplates.CLASSIC]: {
				columns: ['Broker commission', `${this.getDescription(invoiceItems)} (${this.invoiceablePercentage}%)`],
			},
			[InvoiceTemplates.NEO]: {
				columns: [
					` — Broker commission (${this.invoiceablePercentage}%)`,
					`${formatPercentage(this.percentage, 2, false)}`,
					'%',
					`${formatCurrency(
						ensureNegative(totalFreight),
						this.fixtureCurrency,
						{ hideSymbol: true },
					)}`,
				],
			},
		};
	}
}

HireInvoiceItem.addItemClass(HIIFreightBrokerCommission);

export default HIIFreightBrokerCommission;
